import React, { useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, XCircle, RotateCcw, AlertTriangle, Globe, CreditCard } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import SubscribeHardSell from "../../components/SubscribeHardSell";
import { CURRENCIES, useFxRates, convert } from "../../lib/currency";

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return null; }
};

export default function Billing() {
  const { company, reloadCompany } = useOutletContext();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const active = company?.subscription_status === "active";
  const freeAccess = company?.free_access;
  const cancelling = company?.cancel_at_period_end;
  const endDate = fmtDate(company?.current_period_end);
  const currency = company?.display_currency || "GBP";
  const rates = useFxRates();
  const planId = company?.plan === "lite" ? "lite" : "pro";
  const planName = planId === "lite" ? "Lite" : "Pro";
  const planPrice = planId === "lite" ? 45.99 : 75.99;
  const priceStr = convert(planPrice, currency, rates);
  const [upBusy, setUpBusy] = useState(false);
  const upgradeToPro = async () => {
    setUpBusy(true);
    try { const res = await api.post("/payments/checkout", { origin_url: window.location.origin, plan: "pro" }); window.location.href = res.data.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start upgrade"); setUpBusy(false); }
  };

  const [cancelOpen, setCancelOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [curBusy, setCurBusy] = useState(false);

  const changeCurrency = async (val) => {
    setCurBusy(true);
    try { await api.patch("/company/me/currency", { display_currency: val }); await reloadCompany?.(); toast.success("Display currency updated"); }
    catch { toast.error("Could not update currency"); }
    setCurBusy(false);
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post("/payments/cancel");
      await reloadCompany?.();
      setCancelOpen(false);
      toast.success("Subscription cancelled — you won't be charged again.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
    setBusy(false);
  };

  const doResume = async () => {
    setBusy(true);
    try {
      await api.post("/payments/resume");
      await reloadCompany?.();
      toast.success("Subscription resumed — welcome back!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resume"); }
    setBusy(false);
  };

  const doClose = async () => {
    setBusy(true);
    try {
      await api.delete("/company");
      toast.success("Your account has been closed.");
      try { await logout(); } catch {}
      navigate("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close account"); setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="max-w-3xl mx-auto w-full">
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-4">BILLING</h1>
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Current status</p>
            <p className="font-display text-2xl tracking-wide capitalize mt-1">{freeAccess ? "Free access" : company?.subscription_status}</p>
          </div>
          <span className={`text-xs uppercase tracking-wide px-3 py-1.5 rounded-sm font-semibold ${active || freeAccess ? "bg-green-500/15 text-green-400" : "bg-primary/15 text-primary"}`}>
            {freeAccess ? "Owner · Free" : active ? "Subscribed" : company?.has_access ? "On trial" : "Inactive"}
          </span>
        </div>

        <div className="bg-[#18181b] border border-white/10 rounded-sm p-4 mt-3 flex items-center justify-between gap-4 flex-wrap" data-testid="currency-switcher">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Globe className="h-4 w-4 text-primary" /> Show prices in
          </div>
          <div className="flex items-center gap-3">
            <Select value={currency} onValueChange={changeCurrency} disabled={curBusy}>
              <SelectTrigger data-testid="currency-select" className="w-[220px] rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`currency-opt-${c.code}`}>{c.symbol} {c.label} ({c.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {currency !== "GBP" && (
          <div className="mt-2 flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-400" />
            <p className="text-xs text-muted-foreground">Prices are shown in {currency} as a guide. Our plans are priced in <span className="text-foreground font-medium">£GBP</span>; at checkout Stripe shows the exact amount in your local currency (or you can switch to GBP), so there are no surprises on your statement.</p>
          </div>
        )}
      </div>

      {active || freeAccess ? (
        <div className="max-w-3xl mx-auto w-full space-y-6">
          <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-5 text-sm text-green-400 flex items-center gap-2" data-testid="subscribed-banner">
            <Check className="h-4 w-4" /> {freeAccess ? "You have free owner access. Ava is live and answering." : "You're subscribed. Ava is live and answering for your business."}
          </div>

          {/* Manage subscription */}
          {!freeAccess && (
            <div className="bg-[#18181b] border border-white/10 rounded-sm p-6" data-testid="manage-subscription">
              <p className="font-display text-2xl tracking-tight mb-1">MANAGE SUBSCRIPTION</p>
              <p className="text-sm text-muted-foreground mb-5">TradieDesk {planName} · £{planPrice}/month{priceStr.isConverted ? ` (≈ ${priceStr.text})` : ""} · renews automatically{endDate && !cancelling ? ` — next payment ${endDate}` : ""}.</p>

              {planId === "lite" && !cancelling && (
                <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 mb-5" data-testid="upgrade-pro">
                  <p className="text-sm mb-3">You're on <span className="font-semibold">Lite</span>. Upgrade to <span className="text-primary font-semibold">Pro</span> for 500 credits/mo, AI follow-ups, profit tracking + charts, the website embed and all Ava tones.</p>
                  <Button data-testid="upgrade-pro-btn" onClick={upgradeToPro} disabled={upBusy} className="rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold gap-2">
                    {upBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Upgrade to Pro — £75.99/mo
                  </Button>
                </div>
              )}

              {cancelling ? (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4" data-testid="cancel-scheduled">
                  <p className="text-sm text-amber-400 flex items-start gap-2 mb-4">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    Your subscription is set to cancel{endDate ? ` on ${endDate}` : " at the end of your current period"}. Ava will keep working until then, and you won't be charged again.
                  </p>
                  <Button data-testid="resume-btn" onClick={doResume} disabled={busy} className="rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold gap-2">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Keep my subscription
                  </Button>
                </div>
              ) : (
                <>
                  <div className="bg-[#141416] border border-white/10 rounded-sm p-4 mb-5">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">How cancelling works</p>
                    <ol className="space-y-2 text-sm text-muted-foreground list-none">
                      {[
                        "Click \u201CCancel subscription\u201D below.",
                        "Confirm — you won't be charged again.",
                        `Ava keeps working until the end of your paid month${endDate ? ` (${endDate})` : ""}.`,
                        "Changed your mind? Click \u201CKeep my subscription\u201D any time before then.",
                      ].map((s, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <span className="h-5 w-5 rounded-sm bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>{s}
                        </li>
                      ))}
                    </ol>
                  </div>
                  <Button data-testid="cancel-subscription-btn" onClick={() => setCancelOpen(true)} variant="outline" className="rounded-sm border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 font-semibold gap-2">
                    <XCircle className="h-4 w-4" /> Cancel subscription
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Close account */}
          {!freeAccess && (
            <div className="bg-[#141416] border border-red-500/20 rounded-sm p-6" data-testid="close-account-card">
              <p className="font-display text-xl tracking-tight mb-1 text-red-400">CLOSE ACCOUNT</p>
              <p className="text-sm text-muted-foreground mb-4">Permanently delete your business, leads, bookings and settings, and cancel your subscription immediately. This cannot be undone.</p>
              <Button data-testid="close-account-btn" onClick={() => { setConfirmText(""); setCloseOpen(true); }} variant="outline" className="rounded-sm border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 font-semibold gap-2">
                <AlertTriangle className="h-4 w-4" /> Close my account
              </Button>
            </div>
          )}
        </div>
      ) : (
        <SubscribeHardSell
          heading="UNLOCK THE FULL EXPERIENCE"
          currency={currency}
          subheading={company?.has_access
            ? "You're on the free trial. Subscribe now to keep Ava working for you once your trial ends — no interruption."
            : "Subscribe to switch Ava on full-time. Never lose another job to a missed message."}
        />
      )}

      {/* Cancel confirm dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-[#18181b] border-white/10 text-foreground" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Cancel your subscription?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              You'll keep full access until{endDate ? ` ${endDate}` : " the end of your current paid period"}. After that Ava stops answering and you won't be charged again. No refund for the current period — and you can resubscribe anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button data-testid="cancel-dialog-keep" variant="outline" onClick={() => setCancelOpen(false)} className="rounded-sm border-white/10">Keep subscription</Button>
            <Button data-testid="cancel-dialog-confirm" onClick={doCancel} disabled={busy} className="rounded-sm bg-red-500 hover:bg-red-600 text-white font-semibold gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Yes, cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close account confirm dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="bg-[#18181b] border-white/10 text-foreground" data-testid="close-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight text-red-400">Close your account?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This permanently deletes your business, all leads, bookings and settings, cancels your subscription immediately, and signs you out. This <span className="text-red-400 font-semibold">cannot be undone</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <p className="text-sm text-muted-foreground mb-2">Type <span className="font-mono font-semibold text-foreground">DELETE</span> to confirm.</p>
            <Input data-testid="close-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="rounded-sm bg-[#0f0f11] border-white/10" />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button data-testid="close-dialog-keep" variant="outline" onClick={() => setCloseOpen(false)} className="rounded-sm border-white/10">Keep my account</Button>
            <Button data-testid="close-dialog-confirm" onClick={doClose} disabled={busy || confirmText !== "DELETE"} className="rounded-sm bg-red-500 hover:bg-red-600 text-white font-semibold gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />} Permanently close account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
